/**
 * ImpactAreaLevelCharts.jsx
 * Standalone exportable chart components for Impact Area level distributions.
 * One component per Impact Area, suitable for chart registry & ExportRenderer.
 */

import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

const IMPACT_AREAS = [
  { key: 'gender',       label: 'Gender Equality',                    field: 'genderLevel',       color: '#009688' },
  { key: 'climate',      label: 'Climate Adaptation & Mitigation',    field: 'climateLevel',      color: '#2196F3' },
  { key: 'nutrition',    label: 'Nutrition, Health & Food Security',  field: 'nutritionLevel',    color: '#FF9800' },
  { key: 'biodiversity', label: 'Environmental Health & Biodiversity', field: 'biodiversityLevel', color: '#4CAF50' },
  { key: 'poverty',      label: 'Poverty Reduction',                  field: 'povertyLevel',      color: '#9C27B0' },
];

const LEVEL_ORDER = ['Significant', 'Principal'];

function lightenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

function getLevelColor(level, iaColor) {
  if (level === 'Not Targeted') return '#D1D5DB';
  if (level === 'Significant') return lightenColor(iaColor, 0.45);
  return iaColor;
}

/** Factory: creates a standalone chart component for a given Impact Area key. */
function createIaLevelChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaLevelExportChart() {
    const { filteredResults } = useApp();

    const levelData = useMemo(() => {
      if (!ia) return [];
      return LEVEL_ORDER.map(level => ({
        level,
        count: filteredResults.filter(r => r[ia.field] === level).length,
      }));
    }, [filteredResults]);

    if (!ia || levelData.length === 0) {
      return <p className="text-sm text-gray-400 italic py-4 text-center">No data available.</p>;
    }

    return (
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{ia.label} — Level Distribution</h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={levelData} margin={{ top: 20, right: 32, left: 0, bottom: 4 }}>
            <XAxis dataKey="level" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={36} />
            <Tooltip formatter={(value) => [value, 'Results']} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {levelData.map((entry) => (
                <Cell key={entry.level} fill={getLevelColor(entry.level, ia.color)} />
              ))}
              <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };
}

export const GenderLevelChart = createIaLevelChart('gender');
export const ClimateLevelChart = createIaLevelChart('climate');
export const NutritionLevelChart = createIaLevelChart('nutrition');
export const BiodiversityLevelChart = createIaLevelChart('biodiversity');
export const PovertyLevelChart = createIaLevelChart('poverty');
